import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ServiceStatusClient, StatusServiceDTO, ServiceStatusLevel } from 'src/app/base/models/models.swagger.generated';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { StatusBadgeComponent } from '../shared/status-badge/status-badge.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-status-dashboard',
  templateUrl: './status-dashboard.component.html',
  styleUrls: ['./status-dashboard.component.scss'],
  imports: [TranslateModule, StatusBadgeComponent]
})
export class StatusDashboardComponent implements OnInit, OnDestroy {
  public services: StatusServiceDTO[] = [];
  public overallStatus: ServiceStatusLevel = ServiceStatusLevel.Operational;
  public loading = true;
  public error = false;
  public readonly ServiceStatusLevel = ServiceStatusLevel;

  private destroy$ = new Subject<void>();

  constructor(
    private serviceStatusClient: ServiceStatusClient,
    private spinnerService: SpinnerService
  ) {}

  public ngOnInit(): void {
    this.loadServices();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get overallStatusClass(): string {
    switch (this.overallStatus) {
      case ServiceStatusLevel.Operational:
        return 'overall-operational';
      case ServiceStatusLevel.Degraded:
        return 'overall-degraded';
      case ServiceStatusLevel.Down:
        return 'overall-down';
      default:
        return '';
    }
  }

  public get overallStatusKey(): string {
    switch (this.overallStatus) {
      case ServiceStatusLevel.Operational:
        return 'i18n.domain.status-dashboard.overall.operational';
      case ServiceStatusLevel.Degraded:
        return 'i18n.domain.status-dashboard.overall.degraded';
      case ServiceStatusLevel.Down:
        return 'i18n.domain.status-dashboard.overall.down';
      default:
        return '';
    }
  }

  public get sortedServices(): StatusServiceDTO[] {
    return [...this.services].sort((a, b) => {
      const order = (s: ServiceStatusLevel | undefined): number => {
        switch (s) {
          case ServiceStatusLevel.Down: return 0;
          case ServiceStatusLevel.Degraded: return 1;
          case ServiceStatusLevel.Operational: return 2;
          default: return 3;
        }
      };
      return order(a.effectiveStatus) - order(b.effectiveStatus);
    });
  }

  public get operationalCount(): number {
    return this.services.filter(s => s.effectiveStatus === ServiceStatusLevel.Operational).length;
  }

  public get degradedCount(): number {
    return this.services.filter(s => s.effectiveStatus === ServiceStatusLevel.Degraded).length;
  }

  public get downCount(): number {
    return this.services.filter(s => s.effectiveStatus === ServiceStatusLevel.Down).length;
  }

  public retry(): void {
    this.error = false;
    this.loading = true;
    this.loadServices();
  }

  private loadServices(): void {
    this.spinnerService.show();
    this.serviceStatusClient.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (services) => {
          this.services = services ?? [];
          this.calculateOverallStatus();
          this.loading = false;
          this.spinnerService.hide();
        },
        error: () => {
          this.error = true;
          this.loading = false;
          this.spinnerService.hide();
        }
      });
  }

  private calculateOverallStatus(): void {
    if (this.services.some(s => s.effectiveStatus === ServiceStatusLevel.Down)) {
      this.overallStatus = ServiceStatusLevel.Down;
    } else if (this.services.some(s => s.effectiveStatus === ServiceStatusLevel.Degraded)) {
      this.overallStatus = ServiceStatusLevel.Degraded;
    } else {
      this.overallStatus = ServiceStatusLevel.Operational;
    }
  }
}
